import {
  GroupType,
  RewardType,
  QuestGroup,
  PointsResponse,
  RewardGroup,
  ClaimRewardsRequest,
  ClaimRewardsResponse,
  ClaimRewardsPartialResponse,
  NotifyQuestCompleteRequest,
  PurchasePassRequest,
  PurchasePassResponse,
} from '../types/battlepass';
import { config, shouldLog } from '../config/config';

interface ApiError {
  error: {
    code: string;
    message: string;
    details?: Record<string, unknown>;
  };
}

export class BattlePassService {
  private readonly baseUrl: string;
  private readonly headers: HeadersInit;
  private readonly timeout: number;

  constructor(baseUrl: string, token?: string) {
    this.baseUrl = baseUrl;
    this.timeout = config.api.timeout;
    this.headers = {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  }

  /**
   * 獲取 API 基礎 URL（用於 DEBUG）
   */
  getBaseUrl(): string {
    return this.baseUrl;
  }

  /**
   * 獲取指定組別的任務列表
   * @param group - 任務組別（DAY|MONTH）
   */
  async getQuests(group: GroupType): Promise<QuestGroup> {
    const url = `${this.baseUrl}/battlepass/quests?group=${group}`;

    try {
      const response = await this.fetchWithTimeout(url, {
        headers: this.headers,
      });

      if (!response.ok) {
        throw await this.handleApiError(response, `獲取 ${group} 任務列表`);
      }

      const data = await response.json();
      this.logApiCall('GET', url, 'SUCCESS', data);
      return data;
    } catch (error) {
      this.logApiCall('GET', url, 'ERROR', error);
      throw error;
    }
  }

  /**
   * 獲取用戶當前的點數
   */
  async getPoints(): Promise<PointsResponse> {
    const url = `${this.baseUrl}/battlepass/points`;

    try {
      const response = await this.fetchWithTimeout(url, {
        headers: this.headers,
      });

      if (!response.ok) {
        throw await this.handleApiError(response, '獲取用戶點數');
      }

      const data = await response.json();
      this.logApiCall('GET', url, 'SUCCESS', data);
      return data;
    } catch (error) {
      this.logApiCall('GET', url, 'ERROR', error);
      throw error;
    }
  }

  /**
   * 獲取指定組別和類型的獎勵列表
   * @param group - 獎勵組別（DAY|MONTH）
   * @param type - 獎勵類型（free|paid）
   */
  async getRewards(group: GroupType, type: RewardType): Promise<RewardGroup> {
    const url = `${this.baseUrl}/battlepass/rewards?group=${group}&type=${type}`;

    try {
      const response = await this.fetchWithTimeout(url, {
        headers: this.headers,
      });

      if (!response.ok) {
        throw await this.handleApiError(response, `獲取 ${group} ${type} 獎勵列表`);
      }

      const data = await response.json();
      this.logApiCall('GET', url, 'SUCCESS', data);
      return data;
    } catch (error) {
      this.logApiCall('GET', url, 'ERROR', error);
      throw error;
    }
  }

  /**
   * 領取獎勵
   * @param request - 領取獎勵請求 {group: "DAY|MONTH", reward_ids: ["string"]}
   */
  async claimRewards(request: ClaimRewardsRequest): Promise<ClaimRewardsResponse | ClaimRewardsPartialResponse> {
    const url = `${this.baseUrl}/battlepass/rewards/claim`;

    try {
      const response = await this.fetchWithTimeout(url, {
        method: 'POST',
        headers: this.headers,
        body: JSON.stringify(request),
      });

      // 處理成功 (200) 和部分成功 (202) 回應
      if (!response.ok && response.status !== 202) {
        throw await this.handleApiError(response, '領取獎勵');
      }

      const data = await response.json();
      this.logApiCall('POST', url, response.status === 202 ? 'PARTIAL_SUCCESS' : 'SUCCESS', data);
      return data;
    } catch (error) {
      this.logApiCall('POST', url, 'ERROR', error);
      throw error;
    }
  }

  /**
   * 通知任務完成
   * 注意：雖然這是內部 API，但似乎需要前端調用來觸發積分更新
   */
  async notifyQuestComplete(request: NotifyQuestCompleteRequest): Promise<QuestGroup> {
    const url = `${this.baseUrl}/battlepass/quests/complete`;

    try {
      const response = await this.fetchWithTimeout(url, {
        method: 'POST',
        headers: this.headers,
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        throw await this.handleApiError(response, '通知任務完成');
      }

      const data = await response.json();
      this.logApiCall('POST', url, 'SUCCESS', data);
      return data;
    } catch (error) {
      this.logApiCall('POST', url, 'ERROR', error);
      throw error;
    }
  }

  /**
   * 購買 Battle Pass
   * @param request - 購買請求
   */
  async purchasePass(request: PurchasePassRequest): Promise<PurchasePassResponse> {
    const url = `${this.baseUrl}/battlepass/purchase`;

    try {
      const response = await this.fetchWithTimeout(url, {
        method: 'POST',
        headers: this.headers,
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        throw await this.handleApiError(response, '購買 Battle Pass');
      }

      const data = await response.json();
      this.logApiCall('POST', url, 'SUCCESS', data);
      return data;
    } catch (error) {
      this.logApiCall('POST', url, 'ERROR', error);
      throw error;
    }
  }

  private async fetchWithTimeout(url: string, options: RequestInit): Promise<Response> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);

    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      return response;
    } catch (error) {
      clearTimeout(timeoutId);
      if ((error as Error).name === 'AbortError') {
        throw new Error(`請求超時：${url} (${this.timeout}ms)`);
      }
      throw error;
    }
  }

  private async handleApiError(response: Response, operation: string): Promise<Error> {
    try {
      const errorData: ApiError = await response.json();
      const errorCode = errorData.error?.code || 'UNKNOWN_ERROR';
      const errorMessage = errorData.error?.message || response.statusText;

      return new Error(`${operation}失敗：[${errorCode}] ${errorMessage} (HTTP ${response.status})`);
    } catch {
      return new Error(`${operation}失敗：${response.statusText} (HTTP ${response.status})`);
    }
  }

  private logApiCall(method: string, url: string, status: string, data?: unknown): void {
    if (shouldLog('debug')) {
      console.log(`[BattlePassService] ${method} ${url} - ${status}`, data);
    }
  }
}
